/**
 * EP-99 — LIFF Win-Pickup request screen.
 *
 * Layout (wireframe intent):
 *   1. Fare-board card (image or "not set" fallback)
 *   2. Primary "เรียกวิน" button
 *   3. Status pill (pending → accepted ✅ | expired | cancelled)
 *   4. Ghost cancel button while pending
 *
 * Gate: single-gate on `accountSettings.win_pickup_enabled`.
 * The server returns 403 when the module is off; we detect that here and
 * show a disabled state instead of an error.
 *
 * EP99_D4: NEVER render any rider identity — the API payload contains only
 * `id` and `status`; there is no rider field at any layer.
 */

import { useTranslation } from 'react-i18next'
import { PhotoIcon } from '@heroicons/react/24/outline'
import LoadingSpinner from '@/components/LoadingSpinner'
import {
  useWinPickupFareBoard,
  useCreateWinPickupRequest,
  useCancelWinPickupRequest,
  useWinPickupRequestPoll,
  useWinPickupSession,
  POLL_CAP_MS,
} from '@/hooks/useWinPickup'
import type { AxiosError } from 'axios'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function is403(error: unknown): boolean {
  return (error as AxiosError)?.response?.status === 403
}

function isExpiredByTime(createdAt: string): boolean {
  return Date.now() - new Date(createdAt).getTime() >= POLL_CAP_MS
}

// ---------------------------------------------------------------------------
// Fare-board card
// ---------------------------------------------------------------------------

interface FareBoardCardProps {
  imageUrl: string | null
}

function FareBoardCard({ imageUrl }: FareBoardCardProps) {
  const { t } = useTranslation('win_pickup')

  return (
    <div className="flat-card overflow-hidden" data-testid="fare-board-card">
      <p className="px-4 pt-4 text-xs font-semibold text-base-content/50 uppercase tracking-wide">
        {t('fare_board_title')}
      </p>

      {imageUrl ? (
        <img
          src={imageUrl}
          alt={t('fare_board_title')}
          className="w-full object-contain max-h-64 mt-2"
          data-testid="fare-board-image"
        />
      ) : (
        <div
          className="mx-4 mb-4 mt-2 flex flex-col items-center justify-center gap-2 h-32 rounded-lg bg-base-200 text-base-content/40"
          data-testid="fare-board-not-set"
        >
          <PhotoIcon className="w-8 h-8" aria-hidden="true" />
          <p className="text-sm">{t('fare_board_not_set')}</p>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Status pill (no rider info — EP99_D4)
// ---------------------------------------------------------------------------

type DisplayStatus = 'pending' | 'accepted' | 'expired' | 'cancelled'

interface StatusPillProps {
  status: DisplayStatus
}

function StatusPill({ status }: StatusPillProps) {
  const { t } = useTranslation('win_pickup')

  const badgeClass =
    status === 'accepted'
      ? 'status-badge-success'
      : status === 'expired' || status === 'cancelled'
        ? 'status-badge-ghost'
        : 'status-badge-warning'

  return (
    <span
      className={`${badgeClass} h-auto min-h-5 py-0.5 leading-tight whitespace-nowrap inline-block`}
      data-testid={`status-pill-${status}`}
      role="status"
      aria-live="polite"
    >
      {t(`status.${status}`)}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Active request view (poll + cancel)
// ---------------------------------------------------------------------------

interface ActiveRequestViewProps {
  requestId: number
  createdAt: string
  onClear: () => void
}

function ActiveRequestView({ requestId, createdAt, onClear }: ActiveRequestViewProps) {
  const { t } = useTranslation('win_pickup')
  const { t: tc } = useTranslation('common')

  const { data, isLoading } = useWinPickupRequestPoll({ id: requestId, createdAt })

  const { mutate: cancel, isPending: isCancelling, isError: cancelError } =
    useCancelWinPickupRequest()

  // Derive display status:
  //   1. If the poll has returned a non-pending status, use it.
  //   2. If the 10-min wall-clock cap is reached (even before the server marks it),
  //      show "expired" so the UI never hangs in "pending" forever.
  //   3. Otherwise "pending".
  let displayStatus: DisplayStatus = 'pending'
  if (data && data.status !== 'pending') {
    displayStatus = data.status as DisplayStatus
  } else if (isExpiredByTime(createdAt)) {
    displayStatus = 'expired'
  }

  const isTerminal = displayStatus !== 'pending'

  const handleCancel = () => {
    cancel(requestId, {
      onSuccess: () => {
        // Brief delay so the user sees the "cancelled" status before clearing.
        window.setTimeout(onClear, 1500)
      },
    })
  }

  return (
    <div className="space-y-4" data-testid="active-request-view">
      {/* Poll note */}
      {!isTerminal && (
        <p className="text-xs text-base-content/60 text-center">{t('poll_note')}</p>
      )}

      {/* Status */}
      <div className="flex justify-center">
        {isLoading ? (
          <span className="loading loading-spinner loading-sm text-primary" aria-label={tc('status.loading')} />
        ) : (
          <StatusPill status={displayStatus} />
        )}
      </div>

      {/* Cancel (ghost) — only while pending and not loading */}
      {!isTerminal && !isLoading && (
        <div className="flex justify-center">
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={handleCancel}
            disabled={isCancelling}
            data-testid="cancel-button"
            aria-label={t('cancel_button')}
          >
            {isCancelling
              ? <span className="loading loading-spinner loading-xs" />
              : t('cancel_button')}
          </button>
        </div>
      )}

      {/* Cancel error inline */}
      {cancelError && (
        <div className="alert alert-error text-sm" role="alert">
          <span>{t('errors.cancel_failed')}</span>
        </div>
      )}

      {/* After terminal, offer a "back" to create another */}
      {isTerminal && (
        <div className="flex justify-center">
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={onClear}
            data-testid="new-request-button"
          >
            {tc('actions.back')}
          </button>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function WinPickupPage() {
  const { t } = useTranslation('win_pickup')

  const { session, startSession, clearSession } = useWinPickupSession()

  const {
    data: fareBoard,
    isLoading: fareBoardLoading,
    error: fareBoardError,
  } = useWinPickupFareBoard()

  const {
    mutate: createRequest,
    isPending: isCreating,
    error: createError,
    reset: resetCreate,
  } = useCreateWinPickupRequest()

  // ── Module disabled (single-gate) ──────────────────────────────────────────
  // The server returns 403 on fare_board when the module is off.
  if (fareBoardError && is403(fareBoardError)) {
    return (
      <div
        className="p-6 flex flex-col items-center justify-center gap-4 text-center min-h-[200px]"
        data-testid="module-disabled"
      >
        <p className="text-base-content/60 text-sm">{t('module_disabled')}</p>
      </div>
    )
  }

  // ── Loading fare board ─────────────────────────────────────────────────────
  if (fareBoardLoading) {
    return <LoadingSpinner />
  }

  // ── Fare board load error (non-403) ───────────────────────────────────────
  if (fareBoardError) {
    return (
      <div className="p-4">
        <div className="alert alert-error" role="alert">
          <span>{t('errors.load_failed')}</span>
        </div>
      </div>
    )
  }

  const handleCreate = () => {
    resetCreate()
    createRequest(undefined, {
      onSuccess: ({ request, createdAt }) => {
        startSession(request.id, createdAt)
      },
    })
  }

  // Derive create error display
  const createErrorStatus = (createError as AxiosError)?.response?.status
  const createErrorMessage =
    createErrorStatus === 409
      ? t('errors.already_pending')
      : createErrorStatus === 422
        ? t('errors.no_active_lease')
        : createError
          ? t('errors.create_failed')
          : null

  return (
    <div className="p-4 space-y-4 pb-6" data-testid="win-pickup-page">
      {/* 1. Fare-board card */}
      <FareBoardCard imageUrl={fareBoard?.fare_board_url ?? null} />

      {/* 2. Primary action / active request view */}
      {session ? (
        <ActiveRequestView
          requestId={session.requestId}
          createdAt={session.createdAt}
          onClear={clearSession}
        />
      ) : (
        <div className="space-y-3">
          {/* Create error */}
          {createErrorMessage && (
            <div className="alert alert-error text-sm" role="alert">
              <span>{createErrorMessage}</span>
            </div>
          )}

          {/* Request button */}
          <button
            type="button"
            className="btn btn-primary w-full text-lg"
            onClick={handleCreate}
            disabled={isCreating}
            data-testid="request-button"
            aria-label={t('request_button')}
          >
            {isCreating
              ? <span className="loading loading-spinner loading-sm" />
              : t('request_button')}
          </button>
        </div>
      )}
    </div>
  )
}
